<template>
  <v-sheet
    id="corrosion-analyser-wrapper"
    class="mt-4">
    <!-- Filters Component -->
    <corrosion-analyser-filters ref="filters" />

    <!-- Main Content Row -->
    <v-row class="flex-grow-1" style="overflow: hidden; min-height: 0;">
      <!-- Data Grid Section -->
      <v-col :sm="viewerOpened ? 8 : 12" class="h-100" style="display: flex; flex-direction: column;">
        <v-data-table
          ref="tagsTable"
          class="elevation-0 rounded-2 d-flex flex-column table-sticky corrosion-tags-table h-100"
          style="border: 1px solid var(--v-colored_bg-base)"
          background-color="#F3F5F2"
          color="primary"
          :footer-props="{
            showFirstLastPage: true,
            firstIcon: 'mdi-chevron-double-left',
            lastIcon: 'mdi-chevron-double-right',
            prevIcon: 'mdi-chevron-left',
            nextIcon: 'mdi-chevron-right',
            itemsPerPageText: 'Row/Page',
            itemsPerPageOptions: [10, 15, 30, 50, 100, 200, -1],
          }"
          :options.sync="options"
          :headers="corrosionHeaders"
          :items="entries"
          :loading="loading"
          :server-items-length="total"
          @click:row="showTagsInForge"
          checkbox-color="primary"
          show-select>
          <!-- Top Toolbar Slot -->
          <template v-slot:top>
            <v-sheet flat color="transparent" height="auto" class="d-flex mx-4 my-4" style="gap: 8px;">
              <!-- Search Input -->
              <v-text-field
                class="flex-grow-1 search-input"
                style="max-width: 360px"
                v-model="options.search"
                append-icon="mdi-magnify"
                placeholder="Search..."
                single-line
                solo
                dense
                hide-details
                clearable>
              </v-text-field>

              <!-- Active Filters Display -->
              <v-sheet
                v-if="filtersApplied"
                class="px-4"
                color="transparent">
                <div class="d-flex flex-wrap" style="gap: 8px">
                  <template v-for="[key, value] in Object.entries(filters)">
                    <v-sheet
                      v-if="value.length > 0"
                      :key="key"
                      class="d-flex align-center px-4 py-1 rounded-pill"
                      :height="toolbarHeight"
                      style="border: 1px solid var(--v-primary-base)">
                      <v-icon color="primary" small>mdi-filter</v-icon>
                      <span class="subtitle-2 ml-2">{{ getFilterDisplayName(key) }}</span>
                      <v-chip
                        v-for="option in value"
                        :key="option"
                        class="ml-2"
                        color="primary"
                        small
                        label
                        close
                        @click:close="removeFilter(key, option)">
                        {{ option }}
                      </v-chip>
                    </v-sheet>
                  </template>
                </div>
              </v-sheet>

              <v-spacer></v-spacer>

              <!-- Delete Multiple Button -->
              <v-btn
                class="mr-2 rounded-pill"
                color="error"
                solo-inverted
                :height="toolbarHeight"
                v-if="canDeleteMulti()"
                @click="deleteMulti">
                <v-icon left>mdi-delete</v-icon>
                <span class="font-weight-bold">Delete</span>
              </v-btn>

              <!-- Add to Plan Button -->
              <v-btn
                class="mr-2 rounded-pill"
                color="primary"
                :height="toolbarHeight"
                v-if="canAddToPlan()"
                @click="addToPlan">
                <v-icon left>mdi-plus</v-icon>
                <span class="font-weight-bold">Add to plan</span>
              </v-btn>

              <!-- Filters Button -->
              <v-btn
                outlined
                color="primary"
                :height="toolbarHeight"
                @click="openFilters">
                <v-icon left>mdi-filter-outline</v-icon>
                <span class="font-weight-bold">Filters</span>
              </v-btn>
            </v-sheet>
          </template>

          <!-- Thumbnail Column -->
          <template v-slot:[`item.thumbnailUrl`]="{ item }">
            <v-list-item-avatar size="70" class="inspect-thumbnail rounded-lg ma-0">
              <v-tooltip right :z-index="999" nudge-right="-4px">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="thumbnail-wrapper"
                    style="width: 100%; height: 100%">
                    <img
                      class="pa-1"
                      style="cursor: pointer !important"
                      :src="item.heatMapUrl || item.thumbnailUrl || getDefaultThumbnail()"
                      :key="item.heatMapUrl || item.thumbnailUrl" />
                  </div>
                </template>
                <span>
                  <v-img
                    max-width="200"
                    max-height="200"
                    :src="item.heatMapUrl || item.thumbnailUrl || getDefaultThumbnail()"></v-img>
                </span>
              </v-tooltip>
            </v-list-item-avatar>
          </template>

          <!-- Tag ID / Name Column -->
          <template v-slot:[`item.name`]="{ item }">
            <span
              class="primary--text font-weight-medium"
              style="cursor: pointer"
              @click.stop="viewTag(item)">
              {{ item.name }}
            </span>
          </template>

          <!-- Master Tag Name Column -->
          <template v-slot:[`item.masterTagName`]="{ item }">
            <span>
              <p v-if="item.masterTagName" class="ma-0" :title="item.masterTagName">
                {{ $truncateString(item.masterTagName, 20) }}
              </p>
              <p v-else class="ma-0">-</p>
            </span>
          </template>

          <!-- Title Column -->
          <template v-slot:[`item.title`]="{ item }">
            <div
              class="d-flex align-center"
              style="width: 100%; height: 100%; cursor: pointer">
              <v-tooltip
                v-if="item.title"
                right
                :z-index="999"
                nudge-right="-4px">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="h-100 d-flex align-center">
                    <p class="ma-0" style="cursor: pointer !important">
                      {{ $truncateString(item.title, 20) }}
                    </p>
                  </div>
                </template>
                <span>
                  <p>{{ item.title }}</p>
                </span>
              </v-tooltip>
              <p v-else class="ma-0">-</p>
            </div>
          </template>

          <!-- Height From Deck Column -->
          <template v-slot:[`item.heightFromDeck`]="{ item }">
            <span>{{ formatHeight(item.heightFromDeck) }}</span>
          </template>

          <!-- Schedule Column -->
          <template v-slot:[`item.schedule`]="{ item }">
            <span>{{ item.schedule || '-' }}</span>
        </template>

          <!-- Max Distance Column (Color Coded) -->
          <template v-slot:[`item.maxDistance`]="{ item }">
            <v-chip
              v-if="item.maxDistance"
              small
              :color="getMaxDistanceColor(item.maxDistance)"
              class="font-weight-bold">
              {{ item.maxDistance }} mm
            </v-chip>
            <span v-else>-</span>
          </template>

          <!-- Predicted WT Loss Column (Color Coded) -->
          <template v-slot:[`item.predictedWtLoss`]="{ item }">
            <v-chip
              v-if="item.predictedWtLoss"
              small
              :color="getPredictedLossColor(item.predictedWtLoss)"
              class="font-weight-bold">
              {{ item.predictedWtLoss }} mm
            </v-chip>
            <span v-else>-</span>
          </template>

          <!-- Category Column (Color Coded) -->
          <template v-slot:[`item.categoryName`]="{ item }">
            <v-chip
              v-if="item.categoryName"
              small
              :color="getCategoryColor(item)"
              class="font-weight-bold">
              {{ item.categoryName }}
            </v-chip>
            <span v-else>-</span>
          </template>

          <!-- Heat Map Column -->
          <template v-slot:[`item.heatMapUrl`]="{ item }">
            <div v-if="item.heatMapUrl" class="heatmap-thumbnail-container">
              <v-img
                :src="item.heatMapUrl"
                width="80"
                height="60"
                class="heatmap-thumbnail rounded elevation-2"
                @click.stop="viewHeatMap(item)"
                style="cursor: pointer;">
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="20"></v-progress-circular>
                  </v-row>
                </template>
                <div class="heatmap-overlay">
                  <v-icon small color="white">mdi-magnify-plus-outline</v-icon>
                </div>
              </v-img>
            </div>
            <span v-else class="text-caption grey--text">-</span>
          </template>

          <!-- Severity Column (Button with Dropdown) -->
          <template v-slot:[`item.severity`]="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="bg_force_white--text"
                  :color="getSeverityColor(item.severity)"
                  v-bind="attrs"
                  v-on="on"
                  small>
                  <v-icon small class="mr-1">mdi-alert-circle-outline</v-icon>
                  <span>{{ item.severity }}</span>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="priority in priorities"
                  :key="priority"
                  @click="updateSeverity(item, priority)">
                  <v-list-item-title>{{ priority }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <!-- Status Column (Chip with Dropdown) -->
          <template v-slot:[`item.status`]="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  v-on="on"
                  :color="getStatusBackground(item.status)"
                  :style="{ color: getStatusColor(item.status) }"
                  style="min-width: 100px"
                  class="text-center"
                  dense
                  small
                  label>
                  {{ item.status }}
                </v-chip>
              </template>
              <v-list>
                <v-list-item
                  v-for="status in statuses"
                  :key="status"
                  @click="updateStatus(item, status)">
                  <v-list-item-title>{{ status }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <!-- Priority Column (Chip with Dropdown) -->
          <template v-slot:[`item.priority`]="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  v-on="on"
                  :color="getPriorityBackground(item.priority)"
                  :style="{ color: getPriorityColor(item.priority) }"
                  style="min-width: 100px"
                  class="text-center"
                  dense
                  small
                  label>
                  {{ item.priority }}
                </v-chip>
              </template>
              <v-list>
                <v-list-item
                  v-for="priority in priorities"
                  :key="priority"
                  @click="updatePriority(item, priority)">
                  <v-list-item-title>{{ priority }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <!-- Actions Column -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center" style="gap: 8px">
              <v-tooltip top :z-index="999" nudge-right="-4px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="error"
                    icon
                    class="elevation-0 rounded-pill"
                    @click.stop="deleteTag(item)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                Delete
              </v-tooltip>
              <v-tooltip top :z-index="999" nudge-right="-4px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="warning"
                    icon
                    class="elevation-0 rounded-pill"
                    @click.stop="editTag(item)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                Edit
              </v-tooltip>
              <v-tooltip top :z-index="999" nudge-right="-4px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    :color="item.reportUrl ? 'primary' : 'warning'"
                    icon
                    class="elevation-0 rounded-pill"
                    :disabled="reportLoadingId && reportLoadingId !== item.id"
                    :loading="reportLoadingId === item.id"
                    @click.stop="viewTag(item)">
                    <v-icon>mdi-text-box-check-outline</v-icon>
                  </v-btn>
                </template>
                Report
              </v-tooltip>
              <v-tooltip top :z-index="999" nudge-right="-4px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    icon
                    class="elevation-0 rounded-pill"
                    :disabled="!item.point"
                    @click.stop="showTagsInForge(item)">
                    <v-icon>mdi-cube-outline</v-icon>
                  </v-btn>
                </template>
                Show in Viewer
              </v-tooltip>
              <v-tooltip top :z-index="999" nudge-right="-4px" v-if="hasLinked360Image(item)">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    icon
                    class="elevation-0 rounded-pill"
                    :disabled="reportLoadingId && reportLoadingId !== item.id"
                    @click.stop="openTagIn360(item)">
                    <v-icon>mdi-image-filter-center-focus</v-icon>
                  </v-btn>
                </template>
                Open in 360
              </v-tooltip>
            </div>
          </template>

          <!-- No Data State -->
          <template v-slot:no-data>
            <div class="text-center d-flex flex-column py-8">
              <v-icon size="80" color="grey lighten-2">mdi-pipe-leak</v-icon>
              <p class="font-weight-medium mt-4">No Corrosion Data Available</p>
              <p class="grey--text">
                Start by creating AVA-Inspect tags with Corrosion defect type
              </p>
            </div>
          </template>

          <!-- Loading State -->
          <template v-slot:loading>
            <div class="text-center d-flex flex-column py-8">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <p class="font-weight-medium mt-4">Loading Corrosion Analysis...</p>
            </div>
          </template>
        </v-data-table>
      </v-col>

      <!-- Viewer Section -->
      <v-col v-show="viewerOpened" sm="4" class="viewers-section pl-0 h-100" style="display: flex; flex-direction: column;">
        <viewers-section
          ref="viewersSection"
          :viewerTab.sync="viewerTab"
          :usePhotoSphereViewer="false"
          @closeViewersSection="closeViewer">
        </viewers-section>
      </v-col>
    </v-row>

    <!-- Heat Map Dialog -->
    <v-dialog
      v-model="heatMapDialog.visible"
      max-width="1200px"
      persistent
      transition="fade-transition">
      <v-card>
        <v-toolbar dark color="primary" dense>
          <v-icon class="mr-2">mdi-heat-wave</v-icon>
          <v-toolbar-title class="font-weight-bold">
            Corrosion Analysis - {{ heatMapDialog.tagName }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="heatMapDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <div class="d-flex justify-center">
            <v-img
              :src="heatMapDialog.url"
              max-width="100%"
              max-height="70vh"
              contain
              class="rounded elevation-2">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn
            color="primary"
            outlined
            :href="heatMapDialog.url"
            target="_blank"
            download>
            <v-icon left>mdi-download</v-icon>
            Download
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="heatMapDialog.visible = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add to Plan Modal -->
    <add-corrosion-to-plan ref="addToPlan" />
  </v-sheet>
</template>

<script>
  import { $truncateString } from '@/utils';
  import { mapGetters, mapState } from 'vuex';
  import corrosionHeaders from '../config/corrosionAnalyser.headers';
  import ViewersSection from '../components/ViewersSection.vue';
  import CorrosionAnalyserFilters from '../components/CorrosionAnalyserFilters.vue';
  import AddCorrosionToPlan from '../components/AddCorrosionToPlan.vue';
  import { defectCategorization, getCategoryByWTLoss, getCategoryByMaxDistance } from '../config/enums';
  import { showTagsInForge } from '@/utils/forgeMessageUtils';
  import { getFilterOptionDisplayName } from '../config/helper';
  import { PANEL_TAB } from '@/constants/tabs';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'CorrosionAnalyserDashboard',
    components: {
      ViewersSection,
      CorrosionAnalyserFilters,
      AddCorrosionToPlan,
    },
    data() {
      return {
        corrosionHeaders,
        toolbarHeight: 40,
        reportLoadingId: null,
        groupByMasterTag: true,
        processingCount: 0,
        viewerTab: '3D View',
        viewerOpened: false,
        statuses: ['Open', 'In Review', 'Verified', 'Closed'],
        priorities: ['Critical', 'High', 'Medium', 'Low'],
        defectCategorization,
        heatMapDialog: {
          visible: false,
          url: null,
          tagName: null,
        },
      };
    },
    computed: {
      ...mapState('CORROSION_ANALYSER', ['loading']),
      ...mapGetters('CORROSION_ANALYSER', {
        entries: 'GET_ENTRIES',
        total: 'GET_TOTAL',
        groupedEntries: 'GET_GROUPED_ENTRIES',
        statistics: 'GET_STATISTICS',
      }),
      forgeService() {
        return this.$store.state.forgeService;
      },
      viewer() {
        return this.$store.state.viewer;
      },
      isolatedTags() {
        // Try getter first; fallback to module state if used
        return (
          this.$store.getters['CORROSION_ANALYSER/ISOLATED_TAGS'] ||
          this.$store.state?.CORROSION_ANALYSER?.isolatedTags ||
          []
        );
      },
      corrosionViewer: {
        get() {
          return this.$store.getters['CORROSION_ANALYSER/CORROSION_VIEWER'];
        },
        set(val) {
          this.$store.commit('CORROSION_ANALYSER/SET_CORROSION_VIEWER', val);
        },
      },
      options: {
        get() {
          return this.$store.getters['CORROSION_ANALYSER/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('CORROSION_ANALYSER/SET_OPTIONS', val);
        },
      },
      filters: {
        get() {
          return this.options.filters;
        },
      },
      filtersApplied() {
        return Object.values(this.filters).flat().length > 0;
      },
      images() {
        return this.$store.getters['AI_INSPECT/IMAGES'] || [];
      },
    },
    methods: {
      $truncateString,

      /**
       * Filter and display helpers
       */
      getFilterDisplayName(key) {
        return getFilterOptionDisplayName(key);
      },

      /**
       * Check if tag has a linked 360 image
       */
      hasLinked360Image(item) {
        return !!(item?.linked360ImageInfo?.imageId);
      },

      removeFilter(key, option) {
        const filters = { ...this.options.filters };
        filters[key] = filters[key].filter((item) => item !== option);
        this.options = { ...this.options, filters };
      },

      /**
       * Open filters panel
       */
      openFilters() {
        this.$refs.filters.open();
      },

      /**
       * Color coding helpers
       */
      getSeverityColor(severity) {
        switch (`${severity}`) {
          case '1':
          case 'Low':
            return '#215F9B';
          case '2':
          case 'Medium':
            return '#D2A000';
          case '3':
          case 'High':
            return '#C04F15';
          case '4':
          case 'Critical':
            return '#A80000';
          default:
            return '#03624c';
        }
      },

      getStatusColor(status) {
        const colors = {
          Open: 'orange',
          'In Review': 'blue',
          Verified: 'purple',
          Closed: 'green',
        };
        return colors[status] || 'grey';
      },

      getStatusBackground(status) {
        const backgrounds = {
          Open: '#fb8c0024',
          'In Review': '#2196f324',
          Verified: '#9c27b024',
          Closed: '#4caf5024',
        };
        return backgrounds[status] || '#e0e0e024';
      },

      getPriorityColor(priority) {
        switch (priority) {
          case 'Critical':
            return '#A80000';
          case 'High':
            return '#C04F15';
          case 'Medium':
            return '#D2A000';
          case 'Low':
            return '#215F9B';
          default:
            return '#03624c';
        }
      },

      getPriorityBackground(priority) {
        switch (priority) {
          case 'Critical':
            return '#A8000024';
          case 'High':
            return '#C04F1524';
          case 'Medium':
            return '#D2A00024';
          case 'Low':
            return '#215F9B24';
          default:
            return '#03624c24';
        }
      },

      /**
       * Get color for max distance chip based on corrosion categories
       * Uses the standardized corrosion categories from enums.js:
       * - Cat A (low): Max Distance < 4mm -> Green
       * - Cat B (moderate): 4mm ≤ Max Distance < 6mm -> Yellow
       * - Cat C (high): 6mm ≤ Max Distance < 9mm -> Orange
       * - Cat D (critical): Max Distance ≥ 9mm -> Red
       */
      getMaxDistanceColor(distance) {
        const value = parseFloat(distance);
        if (isNaN(value)) return 'grey';
        
        // Get category based on Max Distance (in mm)
        const category = getCategoryByMaxDistance(value);
        
        // Return color based on severity level
        switch (category?.severityLevel) {
          case 'critical':
            return 'error'; // Red for Cat D (≥9mm)
          case 'high':
            return 'warning'; // Orange for Cat C (6-9mm)
          case 'moderate':
            return 'yellow darken-2'; // Yellow for Cat B (4-6mm)
          case 'low':
            return 'success'; // Green for Cat A (<4mm)
          default:
            return 'grey';
        }
      },

      /**
       * Get color for predicted WT loss chip based on corrosion categories
       * Uses the standardized corrosion categories from enums.js:
       * - Cat A (low): WT Loss < 2mm -> Green
       * - Cat B (moderate): 2mm ≤ WT Loss < 3mm -> Yellow
       * - Cat C (high): 3mm ≤ WT Loss < 5mm -> Orange
       * - Cat D (critical): WT Loss ≥ 5mm -> Red
       */
      getPredictedLossColor(loss) {
        const value = parseFloat(loss);
        if (isNaN(value)) return 'grey';
        
        // Get category based on WT Loss (in mm)
        const category = getCategoryByWTLoss(value);
        
        // Return color based on severity level
        switch (category?.severityLevel) {
          case 'critical':
            return 'error'; // Red for Cat D (≥5mm)
          case 'high':
            return 'warning'; // Orange for Cat C (3-5mm)
          case 'moderate':
            return 'yellow darken-2'; // Yellow for Cat B (2-3mm)
          case 'low':
            return 'success'; // Green for Cat A (<2mm)
          default:
            return 'grey';
        }
      },

      /**
       * Get color for category chip
       * Uses the categoryColor from ForgeAvaInspect instance which is computed from
       * the corrosion category based on maxDistance
       */
      getCategoryColor(item) {
        if (!item || !item.categoryColor) return 'grey';
        
        // ForgeAvaInspect has categoryColor getter that returns the hex color
        // Convert hex color to Vuetify color name for consistency
        const hexColor = item.categoryColor;
        
        // Map hex colors to Vuetify color names
        switch (hexColor) {
          case '#ff0000': // Red
            return 'error';
          case '#ffa500': // Orange
            return 'warning';
          case '#ffff00': // Yellow
            return 'yellow darken-2';
          case '#00ff00': // Green
            return 'success';
          default:
            return 'grey';
        }
      },

      getDefectTypeColor(type) {
        switch (type) {
          case 'Pitting':
          case 'Crevice':
            return '#C04F15';
          case 'Uniform':
          case 'Surface Rust':
            return '#D2A000';
          case 'Galvanic':
          case 'Intergranular':
            return '#f8b400';
          default:
            return '#00a86b';
        }
      },

      getDefectTypeBackground(type) {
        switch (type) {
          case 'Pitting':
          case 'Crevice':
            return '#C04F1524';
          case 'Uniform':
          case 'Surface Rust':
            return '#D2A00024';
          case 'Galvanic':
          case 'Intergranular':
            return '#f8b40024';
          default:
            return '#00a86b24';
        }
      },

      getDefectCategorizationColor(item) {
        const val = this.defectCategorization.find((e) => e.name == item?.defectCategorization);
        return val ? val.color : 'default';
      },

      /**
       * Formatting helpers
       */
      formatHeight(value) {
        if (value === null || value === undefined) return '-';
        const units = this.$store.state.userSettings?.displaySettings?.displayUnits || 'm';
        return `${value.toFixed(2)} ${units}`;
      },

      getDefaultThumbnail() {
        return '/img/no-image.png';
      },

      /**
       * Group statistics helpers
       */
      getGroupCriticalCount(items) {
        return items.filter((item) => item.severity === 'Critical').length;
      },

      getGroupHighCount(items) {
        return items.filter((item) => item.severity === 'High').length;
      },

      canDeleteMulti() {
        const selected = Object.values(this.$refs.tagsTable?.selection ?? {});
        return selected.length > 1;
      },

      canAddToPlan() {
        const selected = Object.values(this.$refs.tagsTable?.selection ?? {});
        return selected.length > 0;
      },

      async addToPlan() {
        const selected = Object.values(this.$refs.tagsTable.selection);
        this.$refs.addToPlan.open(selected);
      },

      async deleteMulti() {
        const selected = Object.values(this.$refs.tagsTable?.selection ?? {});
        this.$dialog.error({
          text: `Are you sure you want to delete ${selected.length} tags?`,
          title: 'Delete Tags',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Delete',
              color: 'error',
              handle: async () => {
                try {
                  const ids = selected.map((tag) => tag.id);
                  await this.$store.dispatch('CORROSION_ANALYSER/DELETE_MULTI', ids);
                  this.$dialog.notify.success('Tags deleted successfully', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                } catch (error) {
                  this.$dialog.notify.error('Error deleting tags', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                }
              },
            },
          },
        });
      },

      async deleteTag(tag) {
        this.$dialog.error({
          text: 'Are you sure you want to delete this tag?',
          title: 'Delete Tag',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Delete',
              color: 'error',
              handle: async () => {
                try {
                  await this.$store.dispatch('CORROSION_ANALYSER/DELETE', tag.id);
                  this.$dialog.notify.success('Tag deleted successfully', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                } catch (error) {
                  this.$dialog.notify.error('Error deleting tag', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                }
              },
            },
          },
        });
      },

      startEditing(item, field = 'description') {
        this.$set(item, `${field}Editing`, true);
        this.$set(item, `old${field.charAt(0).toUpperCase() + field.slice(1)}`, item[field]);
      },

      async saveField(item, field) {
        this.$set(item, `${field}Editing`, false);
        const oldValue = item[`old${field.charAt(0).toUpperCase() + field.slice(1)}`];
        if (item[field] === oldValue) return;

        try {
          await this.$store.dispatch('CORROSION_ANALYSER/UPDATE', item);
          this.$dialog.notify.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`, {
            position: 'top-right',
            timeout: 2000,
          });
        } catch (error) {
          this.$dialog.notify.error(`Error updating ${field}`, {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      async saveDescription(item) {
        await this.saveField(item, 'description');
      },

      async saveTitle(item) {
        await this.saveField(item, 'title');
      },

      async updateSeverity(item, severity) {
        item.severity = severity;
        await this.$store.dispatch('CORROSION_ANALYSER/UPDATE', item);
      },

      async updateStatus(item, status) {
        item.status = status;
        await this.$store.dispatch('CORROSION_ANALYSER/UPDATE', item);
      },

      async updatePriority(item, priority) {
        item.priority = priority;
        await this.$store.dispatch('CORROSION_ANALYSER/UPDATE', item);
      },

      /**
       * Viewer integration
       */
      showTagInViewer(tag) {
        this.viewerOpened = true;
        this.$store.commit('CORROSION_ANALYSER/SET_ISOLATED_TAGS', [tag]);

        if (this.viewerTab === '3D View') {
          this.$refs.viewersSection?.load3dViewer().then(() => {
            this.isolateTagIn3D(tag);
          });
        }
      },

      closeViewer() {
        this.viewerOpened = false;
        this.$store.commit('CORROSION_ANALYSER/SET_ISOLATED_TAGS', []);
      },

      isolateTags() {
        const selected = Object.values(this.$refs.tagsTable?.selection ?? {});
        this.viewerOpened = true;
        this.$store.commit('CORROSION_ANALYSER/SET_ISOLATED_TAGS', selected);

        this.$refs.viewersSection?.load3dViewer().then(() => {
          selected.forEach((tag) => this.isolateTagIn3D(tag));
        });
      },

      isolateTagIn3D(tag) {
        if (tag.masterTag) {
          this.forgeService?.zoomInAllAvailableViewers(tag.masterTag, true);
        }
      },

      zoomToMasterTag(item) {
        if (item.masterTag) {
          this.forgeService?.zoomInAllAvailableViewers(item.masterTag, true);
        }
      },

      navigateToTag(item) {
        if (!item.id) return;

        // Navigate to the tag details/edit page
        this.$store
          .dispatch('FETCH_TAG_BY_TYPE_AND_ID', {
            type: 'avaInspect', // or the appropriate tag type
            id: item.id,
          })
          .then((tag) => {
            this.$store.dispatch('OPEN_TAG', { tag: tag.clone(), clearPrevious: true });
          })
          .catch((error) => {
            error('Error navigating to tag:', error);
            this.$dialog.notify.error('Failed to navigate to tag', {
              position: 'top-right',
              timeout: 3000,
            });
          });
      },

      navigateToMasterTag(item) {
        if (!item.masterTagId) return;

        this.$store
          .dispatch('FETCH_TAG_BY_TYPE_AND_ID', {
            type: 'Pipeline',
            id: item.masterTagId,
          })
          .then((tag) => {
            this.$store.dispatch('OPEN_TAG', { tag: tag.clone(), clearPrevious: true });
          })
          .catch((error) => {
            error('Error navigating to master tag:', error);
            this.$dialog.notify.error('Failed to navigate to master tag', {
              position: 'top-right',
              timeout: 3000,
            });
          });
      },

      async openReport(item) {
        if (item.reportUrl) {
          window.open(item.reportUrl, '_blank');
          return;
        }

        this.reportLoadingId = item.id;
        try {
          const result = await this.$store.dispatch('CORROSION_ANALYSER/GENERATE_REPORT', {
            id: item.id,
          });
          if (result.reportUrl) {
            window.open(result.reportUrl, '_blank');
          }
        } catch (error) {
          this.$dialog.notify.error('Error generating report', {
            position: 'top-right',
            timeout: 3000,
          });
        } finally {
          this.reportLoadingId = null;
        }
      },

      /**
       * View heat map - Opens the heat map in a dialog
       */
      viewHeatMap(item) {
        if (!item.heatMapUrl) {
          this.$dialog.notify.warning('No heat map available for this tag', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.heatMapDialog = {
          visible: true,
          url: item.heatMapUrl,
          tagName: item.name || item.title || 'Analysis',
        };
      },

      /**
       * View tag - Opens the tag in the side panel in readonly mode
       * Opens the left panel TagsTab within the dashboard (no navigation)
       */
      async viewTag(item) {
        if (!item.id) return;

        try {
          // Fetch full tag data
          const tag = await this.$store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', item.id);

          // Set the tag as editing tag (will be shown in TagsTab)
          this.$store.commit('SET_EDITING_TAG', tag.clone());

          // Set readonly mode to show info panel
          this.$store.commit('SET_IS_TAG_READ_ONLY', true);

          // Open the left panel to TagsTab
          // Using SHOW_PANEL to bridge both legacy and new panel system
          this.$store.dispatch('SHOW_PANEL', { tabIndex: PANEL_TAB.TAGS });

        } catch (err) {
          error('Error opening tag for view:', err);
          this.$dialog.notify.error('Failed to open tag', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Edit tag - Opens the tag in the side panel for editing
       * Opens the left panel TagsTab within the dashboard (no navigation)
       */
      async editTag(item) {
        if (!item.id) return;

        try {
          // Fetch full tag data
          const tag = await this.$store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', item.id);

          // Set the tag as editing tag (will be shown in TagsTab)
          this.$store.commit('SET_EDITING_TAG', tag.clone());

          // Set edit mode to show edit panel
          this.$store.commit('SET_IS_TAG_READ_ONLY', false);

          // Open the left panel to TagsTab
          // Using SHOW_PANEL to bridge both legacy and new panel system
          this.$store.dispatch('SHOW_PANEL', { tabIndex: PANEL_TAB.TAGS });

        } catch (err) {
          error('Error opening tag for edit:', err);
          this.$dialog.notify.error('Failed to open tag for editing', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Show tag in Forge 3D viewer
       * Opens 3D viewer and either applies crop box or zooms to point
       */
      async showTagsInForge(item) {
        if (!item.id) return;

        try {
          // Fetch full tag to get cropVolumeJson and point data
          const fetchedTag = await this.$store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', item.id);

          // Persist isolated tag so other viewers (e.g., 2D) can use it
          this.$store.commit('CORROSION_ANALYSER/SET_ISOLATED_TAGS', [fetchedTag]);

          // Open viewer section and switch to 3D
          this.viewerOpened = true;
          this.viewerTab = '3D View';

          // Load 3D viewer
          await this.$refs.viewersSection?.load3dViewer();

          // Check if tag has crop box
          if (fetchedTag.cropVolumeJson) {
            // Apply crop box
            const cropExt = this.forgeService?.getCropping3DExtension();
            if (cropExt) {
              const success = cropExt.loadCropVolumeForTag(fetchedTag);
              if (success) {
                this.$dialog.notify.success('Crop box loaded and applied', {
                  position: 'top-right',
                  timeout: 2000,
                });
              }
            }

            this.forgeService?.zoomToPoint3dViewer(fetchedTag.cropBox);
          } else {
            if (fetchedTag) {
              this.forgeService?.zoomToPoint3dViewer(fetchedTag.getPointInView);
            }
          }
        } catch (error) {
          error('Error showing tag in Forge:', error);
          this.$dialog.notify.error('Failed to show tag in viewer', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Open Analyzer - Opens Potree measurement analyzer with crop box
       */
      async openAnalyzer(item) {
        try {
          // Fetch full tag
          const fetchedTag = await this.$store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', item.id);

          // Migrate metadataJson from analysisResultJson for backward compatibility
          if (!fetchedTag.metadataJson && fetchedTag.analysisResult?.pointCloudData?.pointCloudId) {
            const metadata = {
              pointCloudId: fetchedTag.analysisResult.pointCloudData.pointCloudId
            };
            fetchedTag.metadataJson = JSON.stringify(metadata);
            log('[CorrosionAnalyser] Migrated metadataJson with pointCloudId:', metadata.pointCloudId);

            // Update tag with new metadataJson (fire and forget)
            this.$store.dispatch('UPDATE_AVA_INSPECT_TAG', { tag: fetchedTag }).catch(err => {
              warn('[CorrosionAnalyser] Failed to save migrated metadataJson:', err);
            });
          }

          // Parse analysisResult if it's a string
          let analysisResult = fetchedTag.analysisResult;
          if (typeof analysisResult === 'string') {
            try {
              analysisResult = JSON.parse(analysisResult);
            } catch (parseError) {
              error('Error parsing analysisResult:', parseError);
              this.$dialog.notify.error('Invalid analysis data format', {
                position: 'top-right',
                timeout: 3000,
              });
              return;
            }
          }

          // Validate that we have the required data
          if (!analysisResult || !analysisResult.pointCloudData) {
            this.$dialog.notify.error('No point cloud data available for analysis', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          const pointCloudData = analysisResult.pointCloudData;

          // Validate required fields
          if (!pointCloudData.pointCloudUrl || !pointCloudData.cropBox) {
            this.$dialog.notify.error('Missing required point cloud data', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          // Get THREE from window
          const THREE = window.THREE;
          if (!THREE) {
            error('THREE.js not available');
            this.$dialog.notify.error('3D library not loaded', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          // Transform data to the format expected by CroppedMeasurementsDialog
          const cropBox = pointCloudData.cropBox;
          const forgePCInstance = pointCloudData.forgePCInstance || {};
          const globalOffset = pointCloudData.globalOffset || { x: 0, y: 0, z: 0 };

          // Prepare the dialog data
          const dialogData = {
            visible: true,
            cropBox: new THREE.Box3(
              new THREE.Vector3(cropBox.min.x, cropBox.min.y, cropBox.min.z),
              new THREE.Vector3(cropBox.max.x, cropBox.max.y, cropBox.max.z)
            ),
            pointCloudUrl: pointCloudData.pointCloudUrl,
            pointCloudName: pointCloudData.pointCloudName || 'Point Cloud',
            pointCloudId: pointCloudData.pointCloudId,
            forgePCInstance: {
              position: forgePCInstance.position
                ? new THREE.Vector3(
                    forgePCInstance.position.x,
                    forgePCInstance.position.y,
                    forgePCInstance.position.z
                  )
                : new THREE.Vector3(0, 0, 0),
              quaternion: forgePCInstance.quaternion
                ? new THREE.Quaternion(
                    forgePCInstance.quaternion._x,
                    forgePCInstance.quaternion._y,
                    forgePCInstance.quaternion._z,
                    forgePCInstance.quaternion._w
                  )
                : new THREE.Quaternion(),
              scale: forgePCInstance.scale
                ? new THREE.Vector3(
                    forgePCInstance.scale.x,
                    forgePCInstance.scale.y,
                    forgePCInstance.scale.z
                  )
                : new THREE.Vector3(1, 1, 1),
              pcoGeometry: forgePCInstance.pcoGeometry
                ? {
                    offset: forgePCInstance.pcoGeometry.offset
                      ? new THREE.Vector3(
                          forgePCInstance.pcoGeometry.offset.x,
                          forgePCInstance.pcoGeometry.offset.y,
                          forgePCInstance.pcoGeometry.offset.z
                        )
                      : null,
                    boundingBox: forgePCInstance.pcoGeometry.boundingBox
                      ? new THREE.Box3(
                          new THREE.Vector3(
                            forgePCInstance.pcoGeometry.boundingBox.min.x,
                            forgePCInstance.pcoGeometry.boundingBox.min.y,
                            forgePCInstance.pcoGeometry.boundingBox.min.z
                          ),
                          new THREE.Vector3(
                            forgePCInstance.pcoGeometry.boundingBox.max.x,
                            forgePCInstance.pcoGeometry.boundingBox.max.y,
                            forgePCInstance.pcoGeometry.boundingBox.max.z
                          )
                        )
                      : null,
                  }
                : null,
            },
            unitScale: pointCloudData.unitScale !== undefined ? pointCloudData.unitScale : 1,
            globalOffset: new THREE.Vector3(globalOffset.x, globalOffset.y, globalOffset.z),
            analysisResult: analysisResult, // Pass the full analysis result for cylinder and corrosion data
            tagId: item.id, // Pass the AVA Inspect tag ID for update functionality
          };

          log('[CorrosionAnalyser] Opening CroppedMeasurementsDialog with data:', {
            pointCloudName: dialogData.pointCloudName,
            pointCloudUrl: dialogData.pointCloudUrl,
            hasCropBox: !!dialogData.cropBox,
            hasUnitScale: dialogData.unitScale !== null && dialogData.unitScale !== undefined,
            hasGlobalOffset: !!dialogData.globalOffset,
            hasPcoGeometry: !!dialogData.forgePCInstance?.pcoGeometry,
            hasAnalysisResult: !!dialogData.analysisResult,
            hasCylinderData: !!dialogData.analysisResult?.cylinderData,
            hasThresholds: !!dialogData.analysisResult?.thresholds,
            hasStatistics: !!dialogData.analysisResult?.statistics,
            tagId: dialogData.tagId,
          });

          // Open the dialog by committing to Vuex store
          this.$store.commit('SET_CROPPED_MEASUREMENT_DIALOG', dialogData);

          this.$dialog.notify.success('Opening corrosion analyzer...', {
            position: 'top-right',
            timeout: 2000,
          });
        } catch (error) {
          error('Error opening analyzer:', error);
          this.$dialog.notify.error('Failed to open analyzer', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Open tag in 360 viewer
       * Opens the 360 viewer with the linked 360 image for the tag
       */
      async openTagIn360(item) {
        if (!item.id) return;

        try {
          // Fetch full tag data
          const fetchedTag = await this.$store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', item.id);

          // Verify linked360ImageInfo exists
          if (!fetchedTag.linked360ImageInfo?.imageId) {
            this.$dialog.notify.warning('No linked 360 image found for this tag', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          // Persist isolated tag so viewers can use it
          this.$store.commit('CORROSION_ANALYSER/SET_ISOLATED_TAGS', [fetchedTag]);

          // Resolve the actual image object from store
          let linkedImage = this.images.find((img) => img.id == fetchedTag.linked360ImageInfo.imageId);

          // If images not loaded yet, fetch them and retry
          if (!linkedImage) {
            await this.$store.dispatch('AI_INSPECT/GET_IMAGES');
            linkedImage = this.$store.getters['AI_INSPECT/IMAGES']?.find(
              (img) => img.id == fetchedTag.linked360ImageInfo.imageId
            );
          }

          if (!linkedImage) {
            this.$dialog.notify.error('Linked 360 image not found', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          // Open viewer section and switch to 360 View
          this.viewerOpened = true;
          this.viewerTab = '360 View';

          // Wait for ViewersSection to render
          await this.$nextTick();

          // Load the 360 viewer with the linked image
          await this.$refs.viewersSection?.loadPannellumViewer(
            linkedImage,
            fetchedTag,
            'pannellum-viewer'
          );

          this.$dialog.notify.success('Opening 360 viewer...', {
            position: 'top-right',
            timeout: 2000,
          });
        } catch (error) {
          error('Error opening tag in 360 viewer:', error);
          this.$dialog.notify.error('Failed to open 360 viewer', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Data fetching
       */
      getDataDebounced() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.$store.dispatch('CORROSION_ANALYSER/GET');
        }, 400);
      },

      /**
       * Handle viewer tab change similar to AIInspect.vue
       * Ensures the correct viewer is opened when the tab switches
       */
      handleViewerTabChange() {
        if (!this.viewerOpened) return;

        if (this.viewerTab === '3D View') {
          this.$refs.viewersSection?.load3dViewer();
        } else if (this.viewerTab === '2D View') {
          const planUrn = this.$store?.state?.selectedLevel?.planUrn;
          if (planUrn) {
            this.$refs.viewersSection?.load2dViewer(planUrn).then(() => {
              this.onViewer2dSelected();
            });
          }
        } else if (this.viewerTab === '360 View') {
          this.onViewer360Selected();
        }
      },

      /**
       * Handle 360 viewer selection following AIInspect.vue pattern
       * Uses Legacy Pannellum viewer for corrosion tags
       * Prefers linked 360 image if available, otherwise falls back to nearest image
       */
      async onViewer360Selected() {
        if (!this.isolatedTags?.length) return;

        try {
          const tag = this.isolatedTags[0];

          let imageToLoad = null;

          // Method 1: Check for explicitly linked 360 image first
          if (tag.linked360ImageInfo?.imageId) {
            // Resolve the actual image object from store
            imageToLoad = this.images.find((img) => img.id == tag.linked360ImageInfo.imageId);

            // If images not loaded yet, fetch them and retry
            if (!imageToLoad) {
              await this.$store.dispatch('AI_INSPECT/GET_IMAGES');
              imageToLoad = this.$store.getters['AI_INSPECT/IMAGES']?.find(
                (img) => img.id == tag.linked360ImageInfo.imageId
              );
            }
          }

          // Method 2: Fall back to finding closest image by distance
          if (!imageToLoad) {
            imageToLoad = tag.getNearest360Image();
          }

          if (!imageToLoad) {
            this.$dialog?.notify?.warning('No 360 image found near this tag');
            return;
          }

          // Wait for ViewersSection to render the pannellum-viewer container
          await this.$nextTick();

          // Pass the single tag - ViewersSection will handle 3D-to-yaw/pitch conversion
          // Note: Corrosion tags have 3D point coordinates (not 2D pixel coordinates)
          await this.$refs.viewersSection?.loadPannellumViewer(
            imageToLoad,
            tag,  // Pass single tag for 3D point conversion
            'pannellum-viewer'
          );

        } catch (error) {
          error('Error loading 360 viewer for corrosion tag:', error);
          this.$dialog?.notify?.error('Failed to load 360 viewer');
        }
      },

      openViewer() {
        this.viewerOpened = true;
        this.handleViewerTabChange();
      },

      /**
       * Fetch isolated corrosion tags, compute 3D points, find level, load 2D and zoom
       */
      async onViewer2dSelected() {
        try {
          const tags = Array.isArray(this.isolatedTags) ? this.isolatedTags : [];
          if (!tags.length) return;

          // Resolve 3D points for each tag
          const points = [];
          for (const fullTag of tags) {
            let point3d = null;

            // Prefer explicit point on the tag
            if (fullTag?.point) {
              point3d = fullTag.point;
            }

            // If we have a cropBox, use its center as a fallback
            if (!point3d && fullTag?.cropBox) {
              const cb = fullTag.cropBox;
              point3d = {
                x: (cb.min?.x + cb.max?.x) / 2,
                y: (cb.min?.y + cb.max?.y) / 2,
                z: (cb.min?.z + cb.max?.z) / 2,
              };
            }

            if (point3d) points.push(point3d);
          }

          if (!points.length) return;

          // Determine level from first point (extend later for multi-level)
          const level = this.forgeService.getLevelContainsPoint(points[0]) || this.$store.state.levels?.[0];
          if (!level?.planUrn) return;
          this.$store.state.selectedLevel = level;

          // Ensure 2D viewer is loaded and then use centralized root action to zoom
          this.$refs.viewersSection?.load2dViewer(level.planUrn).then(() => {
            if (points.length > 0) {
              this.$store.dispatch('zoomToPointIn2D', { point3d: points[0], shouldZoom: true, callback: () => {
                this.forgeService.getLabels2DExtension()?.createHighlightingTags(tags);
              } });
            }
          });
        } catch (error) {
          error('Error isolating corrosion tags in 2D:', error);
          this.$dialog?.notify?.error?.('Failed to show corrosion tags in 2D view');
        }
      },
    },
    mounted() {
      this.$store.commit('CORROSION_ANALYSER/SET_ENTRIES', []);
      this.$store.commit('CORROSION_ANALYSER/SET_TOTAL', 0);
      // Load 360 images for resolving linked360ImageInfo
      this.$store.dispatch('AI_INSPECT/GET_IMAGES');
    },
    created() {
      this.options.facilityId = this.$route.params.id;
      this.$store.dispatch('CORROSION_ANALYSER/GET');
    },
    watch: {
      options: {
        handler: function () {
          this.getDataDebounced();
        },
        deep: true,
      },
      viewerTab: {
        handler: function () {
          if (!this.viewerOpened) return;
          this.handleViewerTabChange();
        },
      },
    },
    beforeDestroy() {
      clearTimeout(this.debounceTimer);
    },
  };
</script>

<style lang="scss">
  #corrosion-analyser-wrapper {
    height: calc(100vh - 160px);
    display: flex;
    flex-direction: column;

    .search-input {
      .v-input__slot {
        padding: 0 16px;
      }
    }

    .v-toolbar {
      padding: 8px 12px;
    }

    .viewers-section {
      display: flex;
      flex-direction: column;
    }

    .table-sticky {
      overflow: hidden;
      height: 100%;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      border-radius: 8px;

      .v-table__wrapper {
        overflow: overlay;
        height: 100%;
        flex: 1 1 auto;
        padding-bottom: 0;
      }

      .v-data-table-header {
        position: sticky;
        z-index: 6;
        top: 0;
        background: var(--v-light-grey-base) !important;
      }

      .v-data-footer {
        position: relative !important;
        background: var(--v-light-grey-base) !important;
        border-radius: 0px 0px 8px 8px;
      }
    }

    .inspect-thumbnail {
      padding: 4px;

      .thumbnail-wrapper {
        border-radius: 4px;
        background: #dcdcdc;

        & img {
          border-radius: 8px;
        }
      }
    }

    .v-data-table__selected {
      background: #03624c24;
    }
  }

  .theme--light.v-data-table tbody tr:nth-of-type(even) {
    background-color: rgba(0, 0, 0, 0.02);
  }

  .v-tooltip__content.menuable__content__active {
    opacity: 1;
  }

  .item-chip {
    margin: 0px !important;
    padding: 0px !important;
    font-size: 13px;
    min-height: 24px;
  }

  .heatmap-thumbnail-container {
    padding: 4px 0;

    .heatmap-thumbnail {
      position: relative;
      border: 2px solid #e0e0e0;
      transition: all 0.3s ease;

      &:hover {
        border-color: #1976d2;
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;

        .heatmap-overlay {
          opacity: 1;
        }
      }

      .heatmap-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(25, 118, 210, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
        border-radius: inherit;
      }
    }
  }
</style>
